<template>
  <v-dialog
    max-width="600"
    :fullscreen="smAndDown"
    v-model="dialogValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    persistent
    :z-index="1000000"
  >
    <div
      class="tw-relative tw-bg-white tw-py-12 tw-px-14 tw-rounded-2xl tw-w-full tw-max-h-[90dvh] tw-overflow-hidden tw-flex tw-flex-col"
      :class="{
        'tw-px-2 tw-pt-4 tw-pb-4 tw-h-full tw-max-h-none': smAndDown,
        'tw-rounded-none': smAndDown,
      }"
    >
      <div
        class="tw-flex tw-justify-between tw-items-start tw-mb-2 tw-text-gray-500"
      >
        <div>
          <h2 class="tw-text-3xl tw-font-bold tw-mb-3">
            {{ $t('completeProfileModal.title') }}
          </h2>
          <p id="complete-profile-modal-subtitle" class="tw-text-sm tw-mb-6">
            <template v-if="!dynamicSubtitle">
              {{
                $t(
                  `completeProfileModal.${
                    hasConsultations ? 'subtitleHasConsultations' : 'subtitle'
                  }`,
                )
              }}<br />
              <!-- <span class="tw-font-bold">
                {{ $t('completeProfileModal.subtitleBold') }}
              </span> -->
            </template>
            <template v-else>
              <span v-html="dynamicSubtitle"></span>
            </template>
          </p>
        </div>
        <v-btn
          variant="text"
          class="tw-absolute tw-top-0 tw-right-0 tw-m-2"
          @click="openConfirmDialog"
          icon="mdi-close"
        >
        </v-btn>
      </div>

      <div class="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-pr-1 tw-pt-2">
        <Form
          :validation-schema="completeProfileFormSchema"
          :initial-values="initialValues"
          @submit="handleSubmit"
          v-slot="{ errors, meta }"
          class="tw-space-y-6"
          autocomplete="off"
          hide-messages
        >
          <!-- Primeiro Nome -->
          <div>
            <Field name="firstName" v-slot="{ field, errorMessage, value }">
              <v-text-field
                v-bind="field"
                :model-value="value"
                id="firstName"
                :label="$t('completeProfileModal.fields.firstName') + ' *'"
                active
                variant="outlined"
                :error-messages="errorMessage"
                rounded="lg"
                hide-details
                :placeholder="$t('completeProfileModal.placeholders.firstName')"
              />
            </Field>
          </div>

          <!-- Sobrenome -->
          <div>
            <Field name="lastName" v-slot="{ field, errorMessage, value }">
              <v-text-field
                v-bind="field"
                :model-value="value"
                id="lastName"
                :label="$t('completeProfileModal.fields.lastName') + ' *'"
                active
                variant="outlined"
                :error-messages="errorMessage"
                rounded="lg"
                hide-details
                :placeholder="$t('completeProfileModal.placeholders.lastName')"
              />
            </Field>
          </div>

          <!-- Telefone -->
          <div>
            <Field
              name="phoneNumber"
              v-slot="{ field, errorMessage, handleChange, value }"
            >
              <v-text-field
                v-bind="field"
                :model-value="value"
                @update:model-value="
                  (val) => {
                    handleChange(phoneMask(val))
                  }
                "
                id="phoneNumber"
                :label="$t('completeProfileModal.fields.phone') + ' *'"
                active
                :placeholder="$t('completeProfileModal.placeholders.phone')"
                variant="outlined"
                :error-messages="errorMessage"
                rounded="lg"
                maxlength="15"
                hide-details
              />
            </Field>
          </div>

          <!-- Profissão -->
          <div>
            <Field name="profession" v-slot="{ field, errorMessage, value }">
              <v-select
                v-bind="field"
                :model-value="value"
                id="profession"
                :items="professionOptions"
                item-title="title"
                item-value="value"
                :label="$t('completeProfileModal.fields.profession') + ' *'"
                active
                variant="outlined"
                :error-messages="errorMessage"
                rounded="lg"
                :loading="isSpecialtyModelsLoading"
                hide-details
                :placeholder="$t('completeProfileModal.placeholders.profession')"
                persistent-placeholder
                :menu-props="selectMenuProps"
              />
            </Field>
          </div>

          <!-- Estado e Registro Profissional -->
          <div class="tw-flex tw-gap-6 tw-items-end">
            <div class="tw-w-1/3">
              <Field name="crmState" v-slot="{ field, errorMessage, value }">
                <v-select
                  v-bind="field"
                  :model-value="value"
                  id="crmState"
                  :items="brazilianStates"
                  item-title="value"
                  item-value="value"
                  :label="$t('completeProfileModal.fields.crmState') + ' *'"
                  active
                  variant="outlined"
                  :error-messages="errorMessage"
                  rounded="lg"
                  hide-details
                  :placeholder="$t('completeProfileModal.placeholders.crmState')"
                  persistent-placeholder
                  :menu-props="selectMenuProps"
                />
              </Field>
            </div>
            <div class="tw-w-2/3">
              <Field name="crmNumber" v-slot="{ field, errorMessage, value }">
                <v-text-field
                  v-bind="field"
                  :model-value="value"
                  id="crmNumber"
                  :label="$t('completeProfileModal.fields.crmNumber') + ' *'"
                  active
                  variant="outlined"
                  :error-messages="errorMessage"
                  rounded="lg"
                  hide-details
                  :placeholder="$t('completeProfileModal.placeholders.crmNumber')"
                />
              </Field>
            </div>
          </div>

          <div
            class="tw-text-sm tw-transition-colors tw-duration-300"
            :class="meta.valid ? 'tw-text-gray-500' : 'tw-text-danger-600'"
          >
            {{ $t('completeProfileModal.requiredFieldsNote') }}
          </div>

          <!-- Botões -->
          <div class="tw-flex tw-justify-between tw-items-center tw-mt-8">
            <v-btn
              color="primary"
              variant="outlined"
              rounded="xl"
              size="large"
              class="tw-h-[52px] tw-p-0"
              @click="openConfirmDialog"
            >
              <span class="tw-py-[14px] tw-px-6">{{
                $t('completeProfileModal.buttons.cancel')
              }}</span>
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              rounded="xl"
              size="large"
              type="submit"
              class="tw-h-[52px] tw-p-0"
              :loading="isSubmitting"
              :disabled="!meta.valid"
            >
              <span class="tw-py-[14px] tw-px-6">{{
                $t('completeProfileModal.buttons.save')
              }}</span>
            </v-btn>
          </div>
        </Form>
      </div>

      <!-- Dialog de confirmação -->
      <ConfirmDialog
        v-model="showConfirmDialog"
        icon="mdi-alert-outline"
        base-color="red"
        icon-bg-color="tw-bg-danger-500/20"
        :title="$t('completeProfileModal.confirmDialog.title')"
        :message="$t('completeProfileModal.confirmDialog.message')"
        :cancel-button-text="
          $t('completeProfileModal.confirmDialog.cancelButton')
        "
        :confirm-button-text="
          $t('completeProfileModal.confirmDialog.confirmButton')
        "
        @cancel="showConfirmDialog = false"
        @confirm="handleConfirmExit"
        :max-width="'420'"
      />
    </div>
  </v-dialog>
</template>

<script setup>
import { useDisplay } from 'vuetify'
import { Form, Field } from 'vee-validate'
import { useToast } from 'vue-toastification'
import { useI18n } from '@/composables/useI18n'
import { phoneMask } from '@/utils/masks'
import { useUpdateUserKcProfile } from '@/api/users/mutations'
import { createCompleteProfileFormSchema } from '@/composables/schemas/complete-profile-form'
import { useUserStore } from '@/stores/user'
import { useAppStore } from '@/stores/app'
import { useUserMetricsStore } from '@/stores/userMetrics'
import { useModalsStore } from '@/stores/modals'
import { BRAZILIAN_STATES } from '@/constants/brazilianStates'

const userStore = useUserStore()
const appStore = useAppStore()
const userMetricsStore = useUserMetricsStore()
const modalsStore = useModalsStore()
const { profile } = storeToRefs(userStore)
const { hasConsultations } = storeToRefs(userMetricsStore)
const { specialtyModels, isSpecialtyModelsLoading } = storeToRefs(appStore)
const { fromOriginProfileModal, showCompletedProfileModal } =
  storeToRefs(modalsStore)

// Dialog z-index 1000000 (acima do Evidence); menus dos selects precisam ficar acima
const selectMenuProps = {
  zIndex: 1000001,
}

const { smAndDown } = useDisplay()
const toast = useToast()
const { t } = useI18n()
const { mutateAsync: updateUserKcProfile } = useUpdateUserKcProfile()

// Objeto com subtítulos dinâmicos baseados na origem
const setSubtitles = computed(() => ({
  'new-consultation-success': [
    t('completeProfileModal.dynamicSubtitles.newConsultationSuccess.title'),
    t(
      'completeProfileModal.dynamicSubtitles.newConsultationSuccess.description',
    ),
  ],
  'custom-template-modal-btn': [
    t('completeProfileModal.dynamicSubtitles.customTemplateModal.description'),
  ],
  'apply-new-model-btn': [
    t('completeProfileModal.dynamicSubtitles.applyNewModel.description'),
  ],
  evidence: [
    t('completeProfileModal.dynamicSubtitles.evidence.description'),
  ],
}))

// Computed para determinar o subtítulo dinâmico
const dynamicSubtitle = computed(() => {
  if (!fromOriginProfileModal.value) return null

  const subtitle = setSubtitles.value[fromOriginProfileModal.value]
  if (!subtitle) return null

  // Se for um array, converter para HTML
  if (Array.isArray(subtitle)) {
    return subtitle.join('<br/>')
  }

  // Se já for uma string HTML, retornar diretamente
  return subtitle
})

const professionOptions = computed(() => {
  if (!specialtyModels.value?.specialties) return []

  return Object.entries(specialtyModels.value.specialties)
    .map(([specialty, { title }]) => {
      if (specialty === 'generic') {
        return {
          title: 'Médico Generalista',
          value: specialty,
        }
      }
      // Cadastro: uma única opção de psiquiatria (não as variantes de modelo de report)
      if (specialty === 'psychiatry') {
        return {
          title: 'Psiquiatria',
          value: specialty,
        }
      }
      return {
        title,
        value: specialty,
      }
    })
    .filter(({ title, value }) => {
      // Variantes de modelo de report não entram no Cadastro
      if (value === 'psychiatry_assessment') return false

      // Remove especialidades com "laudo" no nome
      if (title.toLowerCase().includes('laudo')) return false

      // Remove especialidades específicas
      const excludedSpecialties = [
        'Avaliação Geriátrica Ampla',
        'Cuidados Paliativos',
        'Discussão de Caso',
        'Consulta de Retorno',
      ]
      if (excludedSpecialties.includes(title)) return false

      return true
    })
    .sort((a, b) => a.title.localeCompare(b.title))
})

const completeProfileFormSchema = computed(() =>
  createCompleteProfileFormSchema(t, professionOptions.value),
)

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue', 'profile-completed'])

const brazilianStates = ref(BRAZILIAN_STATES)

const isSubmitting = ref(false)
const showConfirmDialog = ref(false)

const dialogValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const initialValues = computed(() => {
  const phoneNumber = profile.value.attributes?.phoneNumber || ''
  return {
    firstName: profile.value.firstName || '',
    lastName: profile.value.lastName || '',
    phoneNumber: phoneNumber ? phoneMask(phoneNumber) : '',
    profession: profile.value.attributes?.profession || null,
    crmState: profile.value.attributes?.crmState || null,
    crmNumber: profile.value.attributes?.crmNumber || '',
  }
})

function openConfirmDialog() {
  showConfirmDialog.value = true
}

function handleConfirmExit() {
  showConfirmDialog.value = false
  emit('update:modelValue', false)
  // Limpar a origem quando o modal for fechado
  fromOriginProfileModal.value = null
}

const handleSubmit = async (values) => {
  isSubmitting.value = true

  try {
    const attributes = {
      phoneNumber: values.phoneNumber.replace(/\D/g, ''),
      profession: values.profession,
      crmState: values.crmState,
      crmNumber: values.crmNumber,
      isProfileComplete: 'true',
    }

    await updateUserKcProfile({
      firstName: values.firstName,
      lastName: values.lastName,
      attributes,
    })

    userStore.updateLocalProfile({
      firstName: values.firstName,
      lastName: values.lastName,
      attributes,
    })

    toast.success(t('completeProfileModal.messages.success'))
    emit('profile-completed', values)
    emit('update:modelValue', false)
    fromOriginProfileModal.value = null
    showCompletedProfileModal.value = true
  } catch (error) {
    toast.error(t('completeProfileModal.messages.error'))
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped lang="scss">
:deep() {
  .v-field--focused {
    color: #00b6ce !important;
  }
  .v-input__details {
    padding-left: 0 !important;
  }
}

label {
  font-size: 15px;
  font-weight: 500;
}
</style>
